import { useState, type ReactNode } from "react";
import type { LeadCardResponse, OpportunityView } from "@gioia365/contracts";
import { BookAppointmentDialog, type AppointmentKind } from "../appointments/BookAppointmentDialog";
import { Button } from "../design/components/Button";
import { Dialog } from "../design/components/Dialog";
import { Icon } from "../design/components/Icon";
import { Reason } from "../design/components/Reason";
import { useToast } from "../design/components/Toast";
import { messageForError } from "../i18n/errors";
import { useLocale, useT } from "../i18n/locale";
import { canSetCallback } from "../leads/card/CallbackDialog";
import { useLeadView } from "../leads/lead-queries";
import { canActOnOpportunityOf, opportunityScopeOf, useAccess } from "../session/access";
import type { TabContext } from "../session/tab-context";
import { LostDialog } from "./LostDialog";
import { useChangeOpportunity, useLeadOpportunity } from "./opportunity-queries";
import { isClosedStage, reachedSteps, stageLabel, stageNowText } from "./opportunity-texts";
import { SoldDialog } from "./SoldDialog";
import "./opportunities.css";

// La finestra «Opportunità di {nome}» della scheda (LEAD-2; mock-up `scheda-lead.html`, `openOpportunity`, `renderOpportunity` e
// `OPP_STAGES` di `mockup.js`; SEZ-9, SEZ-10, SEZ-14). La aprono «Opportunità» in testata, «Apri l'opportunità» del riquadro e, una
// volta, l'indirizzo `?opportunita=1` (il dettaglio della pagina «Opportunità»): la stessa finestra, con l'Opportunità di adesso del
// Lead (`useLeadOpportunity`, la lettura del riquadro). Dice le fasi raggiunte (Disco, Demo, Venduto) e «Fase di adesso», poi:
// - nessuna Opportunità, o l'ultima è persa: «Per aprire un'opportunità», «Prenota disco» e «Prenota demo» per chi prenota (la regola
//   di «Prenota disco o demo» in testata), che chiudono la finestra e aprono la prenotazione su quel tipo;
// - aperta, e chi guarda la può cambiare: «Cosa è successo?», «Venduto» e «Persa», su ogni fase aperta, che aprono le loro finestre
//   al posto di questa («Annulla» torna qui; salvate, si chiude tutto e l'avviso lo dice). Gli esiti di disco e demo restano soltanto in
//   «Disco e demo», e la finestra lo dice: nessuna azione due volte (ACCEPTANCE, LEAD-2);
// - aperta, di un collega che chi guarda vede soltanto: il perché; aperta fuori dal suo perimetro: il perché, senza azioni;
// - venduta: la fase finale; chiusa: «Riapri» per chi cambia tutte le Opportunità, con l'esito qui (la pagina sotto è inerte, D16).
// In fondo, che lo status del Lead non cambia mai da solo, e «Chiudi». Il server decide ogni permesso.

type Props = {
  tab: TabContext;
  card: LeadCardResponse;
  /** Il nome della scheda («Giulia Ferri»), per il titolo. */
  leadName: string;
  timeZone: string;
  onClose: () => void;
};

/** Che cosa c'è sullo schermo: la finestra, oppure al suo posto «Venduto», «Persa» o la prenotazione di una disco o di una demo. */
type Step = "window" | "sold" | "lost" | AppointmentKind;

const STEPS = ["disco", "demo", "sold"] as const;

/** Le fasi raggiunte, con il segno di spunta (mock-up `OPP_STEPS`): nella finestra della scheda e nel dettaglio della pagina. */
export function OpportunitySteps({ opportunity, demoSeen }: { opportunity: OpportunityView | null; demoSeen: boolean }) {
  const t = useT();
  const reached = reachedSteps(opportunity, demoSeen);
  return (
    <ol className="g-opportunities__steps" aria-label={t("opportunities.detail.steps")}>
      {STEPS.map((key) => (
        <li key={key} data-reached={reached[key] ? "true" : "false"}>
          <Icon name={reached[key] ? "check" : "dot"} />
          {t(`opportunities.detail.step.${key}`)}
          <span className="g-visually-hidden"> · {reached[key] ? t("opportunities.detail.reached") : t("opportunities.detail.notReached")}</span>
        </li>
      ))}
    </ol>
  );
}

export function OpportunityDialog({ tab, card, leadName, timeZone, onClose }: Props) {
  const t = useT();
  const { locale } = useLocale();
  const access = useAccess();
  const view = useLeadView();
  const toast = useToast();
  const current = useLeadOpportunity(tab, card.lead.id, view, true);
  const change = useChangeOpportunity(tab, card.lead.id, view);
  const [step, setStep] = useState<Step>("window");
  const [failure, setFailure] = useState<string | null>(null);
  const [reopened, setReopened] = useState<string | null>(null);
  const opportunity = current.status === "ready" ? current.opportunity : null;

  /** «Venduto» o «Persa» salvati: si chiude tutto e l'avviso lo conferma; il riquadro si rilegge (`useChangeOpportunity`). */
  function closedNow(message: string) {
    onClose();
    toast.show(message, "success");
  }

  async function reopen() {
    if (!opportunity || change.isPending) return;
    setFailure(null);
    setReopened(null);
    try {
      const response = await change.mutateAsync({ opportunityId: opportunity.id, command: { action: "reopen", body: { version: opportunity.version } } });
      setReopened(t("opportunities.window.reopened", { stage: stageLabel(t, response.opportunity.stage) }));
    } catch (refusal) {
      setFailure(messageForError(t, refusal));
    }
  }

  if (step === "sold" && opportunity) {
    return (
      <SoldDialog
        tab={tab}
        leadId={card.lead.id}
        opportunity={opportunity}
        onClose={() => setStep("window")}
        onSold={() => closedNow(t("opportunities.soldDialog.saved", { status: card.lead.status.name }))}
      />
    );
  }
  if (step === "lost" && opportunity) {
    return <LostDialog tab={tab} leadId={card.lead.id} opportunity={opportunity} onClose={() => setStep("window")} onLost={() => closedNow(t("opportunities.lostDialog.saved"))} />;
  }
  if (step === "disco" || step === "demo") {
    return <BookAppointmentDialog tab={tab} leadId={card.lead.id} timeZone={timeZone} initialKind={step} onClose={onClose} />;
  }

  let body: ReactNode;
  if (current.status === "loading") {
    body = (
      <p className="g-opportunity-window__muted" role="status">
        {t("common.loading")}
      </p>
    );
  } else if (current.status === "error") {
    body = (
      <div className="g-sheet__actions">
        <Reason tone="warning" role="alert">
          {messageForError(t, current.error)}
        </Reason>
        <Button onClick={current.retry}>{t("common.retry")}</Button>
      </div>
    );
  } else if (current.status === "notVisible") {
    body = <Reason>{t("opportunities.box.notVisible")}</Reason>;
  } else {
    const closed = opportunity !== null && isClosedStage(opportunity.stage);
    const open = opportunity !== null && !closed;
    const canChange = open && canActOnOpportunityOf(access, "update", opportunity.assignee.membershipId);
    const canReopen = closed && opportunityScopeOf(access, "update") === "all";
    const canBook = (opportunity === null || opportunity.stage === "lost") && canSetCallback(access, card, view);
    body = (
      <>
        <OpportunitySteps opportunity={opportunity} demoSeen={current.demoSeen} />
        <p>
          {t("opportunities.window.stageNow")} <strong>{stageNowText(t, locale, timeZone, opportunity)}</strong>
        </p>
        {failure ? (
          <Reason tone="warning" role="alert">
            {failure}
          </Reason>
        ) : null}
        {canChange ? (
          <div className="g-opportunity-window__group">
            <p className="g-field__label">{t("opportunities.window.whatHappened")}</p>
            <div className="g-sheet__actions">
              <Button variant="primary" aria-haspopup="dialog" onClick={() => setStep("sold")}>
                {t("opportunities.window.sold")}
              </Button>
              <Button aria-haspopup="dialog" onClick={() => setStep("lost")}>
                {t("opportunities.window.lost")}
              </Button>
            </div>
            <p className="g-opportunity-window__muted">{t("opportunities.window.appointments")}</p>
          </div>
        ) : null}
        {open && !canChange ? <Reason>{t("opportunities.window.readOnly", { name: opportunity.assignee.displayName })}</Reason> : null}
        {opportunity?.stage === "sold" ? <p className="g-opportunity-window__muted">{t("opportunities.window.final")}</p> : null}
        {canBook ? (
          <div className="g-opportunity-window__group">
            <p className="g-field__label">{t("opportunities.window.toOpen")}</p>
            <div className="g-sheet__actions">
              <Button variant="primary" aria-haspopup="dialog" onClick={() => setStep("disco")}>
                {t("opportunities.window.bookDisco")}
              </Button>
              <Button aria-haspopup="dialog" onClick={() => setStep("demo")}>
                {t("opportunities.window.bookDemo")}
              </Button>
            </div>
          </div>
        ) : null}
        {canReopen ? (
          <div className="g-sheet__actions">
            <Button disabled={change.isPending} aria-busy={change.isPending ? "true" : undefined} onClick={() => void reopen()}>
              {t("opportunities.window.reopen")}
            </Button>
          </div>
        ) : null}
        <p className="g-sheet__status" role="status">
          {reopened}
        </p>
      </>
    );
  }

  return (
    <Dialog
      open
      size="wide"
      title={t("opportunities.detail.title", { name: leadName })}
      onClose={onClose}
      footer={
        <>
          <p className="g-opportunity-window__muted">{t("opportunities.window.statusNote")}</p>
          <Button onClick={onClose}>{t("common.close")}</Button>
        </>
      }
    >
      <div className="g-opportunity-window">{body}</div>
    </Dialog>
  );
}
